import { ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { orgMonth } from '@/lib/today';
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDuration, minorToDecimal } from '@cfl/shared';

import {
  Alert, AlertDescription, Button, Input, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import { api, ApiError, toastFailure } from '@/lib/api';
import { money } from '@/lib/invoice-api';

/**
 * What each customer was worth in a month, against the hours that earned it.
 *
 * A row per customer rather than a picker: entering a month's revenue means going
 * down the list, and a screen showing only what is already entered gives no way to
 * see who was MISSED. That is also why the hours are here - a flat amount on its
 * own is a number in a table, while the same amount beside the time it took is an
 * effective rate, and the effective rate is the question somebody opens this for.
 *
 * The hours come from the time entries, not from the amount, so the two figures
 * are derived different ways and can disagree. That is the point: a cross-check
 * built from the same input as the thing it checks proves nothing.
 *
 * Money is minor units end to end. The API sends strings because BigInt does not
 * survive JSON, and the API does every sum - nothing here multiplies money.
 */
interface RevenueRow {
  /** Null when nothing has been recorded for this customer in this month yet. */
  id: string | null;
  customer: { id: string; name: string; currency: string };
  amountMinor: string;
  hourlyRateMinor: string | null;
  comment: string | null;
  seconds: number;
  revenueMinor: string;
  /** Null when no time was logged - dividing by no hours is not a rate of nothing. */
  perHourMinor: string | null;
}

interface RevenueMonth {
  month: string;
  customers: RevenueRow[];
}

/**
 * Minor units into a form field, or an empty field when nothing is set.
 *
 * Empty and zero have to look different here: a customer with no figure yet is
 * not a customer worth nothing.
 */
function toInput(minor: string | null): string {
  if (!minor || minor === '0') return '';
  return minorToDecimal(BigInt(minor));
}

const monthLabel = (month: string) =>
  new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

function shiftMonth(month: string, delta: number): string {
  const [year, index] = month.split('-').map(Number);
  return new Date(Date.UTC(year!, index! - 1 + delta, 1)).toISOString().slice(0, 7);
}

/** One customer's editable fields. An empty string means "cleared", not "unchanged". */
interface Draft {
  amount: string;
  rate: string;
  comment: string;
}

export function RevenuePanel() {
  const queryClient = useQueryClient();
  // A calendar month, not a financial one: this is data entry against a billing
  // cycle, and nobody invoices an April-to-March month.
  const [month, setMonth] = React.useState(() => orgMonth());
  const [search, setSearch] = React.useState('');
  const [drafts, setDrafts] = React.useState<Record<string, Draft>>({});
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['catalog', 'revenue', month],
    queryFn: () => api.get<RevenueMonth>(`/catalog/revenue?month=${month}`),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['catalog', 'revenue'] });

  // Drafts are dropped whenever the month changes, so a figure typed for one month
  // can never be saved against another.
  React.useEffect(() => {
    setDrafts({});
    setNotice(null);
    setError(null);
  }, [month]);

  const rowOf = (row: RevenueRow): Draft =>
    drafts[row.customer.id] ?? {
      amount: toInput(row.amountMinor),
      rate: toInput(row.hourlyRateMinor),
      comment: row.comment ?? '',
    };

  const setField = (row: RevenueRow, field: keyof Draft, value: string) => {
    setNotice(null);
    setDrafts((current) => ({ ...current, [row.customer.id]: { ...rowOf(row), [field]: value } }));
  };

  const changed = (data?.customers ?? []).filter((row) => {
    const draft = drafts[row.customer.id];
    if (!draft) return false;
    return (
      draft.amount !== toInput(row.amountMinor) ||
      draft.rate !== toInput(row.hourlyRateMinor) ||
      draft.comment !== (row.comment ?? '')
    );
  });

  const save = useMutation({
    mutationFn: async () => {
      // One request per changed row, in sequence. A batch endpoint would be a
      // second write path into the same table to save a handful of rows at a time.
      for (const [i, row] of changed.entries()) {
        const draft = drafts[row.customer.id]!;
        try {
          await api.post('/catalog/revenue', {
            customerId: row.customer.id,
            month,
            amount: draft.amount.trim() || '0',
            hourlyRate: draft.rate.trim() === '' ? null : draft.rate.trim(),
            comment: draft.comment.trim() || null,
          });
        } catch (e) {
          // Rows save one at a time, so a refusal part-way leaves the earlier ones
          // stored. Say which customer stopped it and how many went through.
          void invalidate();
          const why = e instanceof ApiError ? e.message : 'could not be saved.';
          throw new ApiError(
            e instanceof ApiError ? e.status : 0,
            `${row.customer.name}: ${why}${i > 0 ? ` The ${i} customer${i === 1 ? '' : 's'} before it ${i === 1 ? 'was' : 'were'} saved.` : ''}`,
          );
        }
      }
    },
    onSuccess: () => {
      const saved = changed.length;
      setDrafts({});
      setError(null);
      setNotice(`Saved ${saved} customer${saved === 1 ? '' : 's'}.`);
      void invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save that.'),
  });

  const copyPrevious = useMutation({
    mutationFn: () =>
      api.post<{ copied: number; from: string }>('/catalog/revenue/copy-previous', { month }),
    onSuccess: (result) => {
      setNotice(
        result.copied === 0
          ? `Nothing to copy from ${monthLabel(result.from)}.`
          : `Copied ${result.copied} figure${result.copied === 1 ? '' : 's'} from ${monthLabel(result.from)}.`,
      );
      void invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not copy that month.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/catalog/revenue/${id}`),
    onSuccess: () => void invalidate(),
    onError: toastFailure('Could not delete that revenue figure'),
  });

  const rows = (data?.customers ?? []).filter((row) =>
    row.customer.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  // Totalled per currency, never across them. `Customer.currency` is per customer
  // and editable, so one non-INR customer would otherwise be added into a sum
  // labelled with somebody else's currency.
  const totals = new Map<string, bigint>();
  for (const row of rows) {
    totals.set(
      row.customer.currency,
      (totals.get(row.customer.currency) ?? 0n) + BigInt(row.revenueMinor),
    );
  }
  const totalSeconds = rows.reduce((sum, row) => sum + row.seconds, 0);
  const totalLabel = [...totals.entries()]
    .filter(([, amount]) => amount > 0n)
    .map(([currency, amount]) => money(amount.toString(), currency))
    .join(' + ');

  return (
    <div className="space-y-4">
      <p className="max-w-prose text-sm text-muted-foreground">
        What a customer was worth in a month. Recorded by hand, not derived from booked
        hours &mdash; the two answer different questions, and a figure that quietly
        recalculated itself would be the wrong one to put in front of a client. The
        hours beside it are what was actually logged, so the two can be compared.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-label="Previous month"
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="min-w-[10rem] text-center text-sm font-semibold">
          {monthLabel(month)}
        </span>
        <Button
          variant="outline"
          size="icon"
          aria-label="Next month"
          onClick={() => setMonth(shiftMonth(month, 1))}
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button hint={"Jump to the current month"} variant="outline" onClick={() => setMonth(orgMonth())}>
          This month
        </Button>

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer"
          className="ml-2 w-56"
        />

        <div className="flex-1" />

        {/* A retainer is the same figure every month. Without this it is every
            customer retyped monthly, which is how a screen ends up never filled in. */}
        <ConfirmButton
          title={`Copy ${monthLabel(shiftMonth(month, -1))} into ${monthLabel(month)}?`}
          description="Every figure from last month is copied across. Customers that already have one this month are left exactly as they are."
          confirmLabel="Copy"
          variant="outline"
          onConfirm={() => copyPrevious.mutate()}
        >
          <Copy className="size-4" />
          Copy previous month
        </ConfirmButton>

        <Button hint={"Save the changed revenue figures"} disabled={changed.length === 0 || save.isPending} onClick={() => save.mutate()}>
          {save.isPending
            ? 'Saving…'
            : changed.length === 0
              ? 'Saved'
              : `Save ${changed.length} change${changed.length === 1 ? '' : 's'}`}
        </Button>
      </div>

      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert>
          <AlertDescription className="text-sm">{notice}</AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-64 w-full" />}

      {data && (
        <div className="overflow-x-auto rounded-md border">
          <Table className="min-w-[980px]">
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead className="w-32">Amount</TableHead>
                <TableHead className="w-32">Hourly rate</TableHead>
                <TableHead className="w-24 text-right">Hours</TableHead>
                <TableHead className="w-32 text-right">Revenue</TableHead>
                <TableHead className="w-28 text-right">Per hour</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const draft = rowOf(row);
                return (
                  <TableRow key={row.customer.id}>
                    <TableCell className="font-medium">
                      {row.customer.name}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {row.customer.currency}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Input
                        inputMode="decimal"
                        className="h-8"
                        placeholder="0.00"
                        value={draft.amount}
                        onChange={(e) => setField(row, 'amount', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        inputMode="decimal"
                        className="h-8"
                        placeholder="0.00"
                        value={draft.rate}
                        onChange={(e) => setField(row, 'rate', e.target.value)}
                      />
                    </TableCell>
                    <TableCell className="ts-num text-right">
                      {row.seconds > 0 ? (
                        formatDuration(row.seconds)
                      ) : (
                        <span className="opacity-30">&mdash;</span>
                      )}
                    </TableCell>
                    <TableCell className="ts-num text-right">
                      {row.revenueMinor === '0' ? (
                        <span className="opacity-30">&mdash;</span>
                      ) : (
                        money(row.revenueMinor, row.customer.currency)
                      )}
                    </TableCell>
                    <TableCell className="ts-num text-right text-muted-foreground">
                      {row.perHourMinor && row.revenueMinor !== '0' ? (
                        money(row.perHourMinor, row.customer.currency)
                      ) : (
                        <span className="opacity-30">&mdash;</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        className="h-8"
                        placeholder="e.g. retainer, PO number"
                        value={draft.comment}
                        onChange={(e) => setField(row, 'comment', e.target.value)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Only where there is something stored to remove. Confirmed,
                          because there is no undo: a deleted figure is one somebody
                          has to remember to type again. */}
                      {row.id && (
                        <ConfirmButton
                          title="Delete this revenue figure?"
                          description={`${row.customer.name}, ${monthLabel(month)}, ${money(row.amountMinor, row.customer.currency)}. This cannot be undone.`}
                          confirmLabel="Delete"
                          onConfirm={() => remove.mutate(row.id!)}
                          size="sm"
                          variant="outline"
                        >
                          Delete
                        </ConfirmButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {rows.length > 0 && (
                <TableRow className="bg-muted/40">
                  <TableCell className="font-semibold" colSpan={3}>
                    Total
                  </TableCell>
                  <TableCell className="ts-num text-right font-semibold">
                    {formatDuration(totalSeconds)}
                  </TableCell>
                  <TableCell className="ts-num text-right font-semibold" colSpan={2}>
                    {totalLabel || '—'}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                    {search ? 'No customer matches that.' : 'No visible customers.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
